import React, { useEffect, useState } from 'react';
import fontkit from '@pdf-lib/fontkit';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { Document, Page, pdfjs } from 'react-pdf';

import SaralLoaderImage from '../../../../../common-components/SaralLoaderImage';
// Import the same fonts as CustomContractPreview
import AlexBrushFont from '../SignContractModal/Fonts/BetterGrade-Regular.ttf';
import MsMadi from '../SignContractModal/Fonts/MsMadi-Regular.ttf';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// Match font URLs with CustomContractPreview
const FONT_URLS = {
  'Dancing Script': 'https://fonts.gstatic.com/s/dancingscript/v25/If2cXTr6YS-zF4S-kcSWSVi_sxjsohD9F50Ruu7BMSoHTQ.ttf',
  Caveat: 'https://fonts.gstatic.com/s/caveat/v18/WnznHAc5bAfYB2QRah7pcpNvOx-pjfJ9SII.ttf',
  Lobster: 'https://fonts.gstatic.com/s/lobster/v30/neILzCirqoswsqX9_oU.ttf',
  Pacifico: 'https://fonts.gstatic.com/s/pacifico/v22/FwZY7-Qmy14u9lezJ96A.ttf',
  'Patrick Hand': 'https://fonts.gstatic.com/s/patrickhand/v23/LDI1apSQOAYtSuYWp8ZhfYeMWQ.ttf',
  'Alex Brush': AlexBrushFont,
  'Ms Madi': MsMadi,
};

const PDFAnnotator = ({ pdfUrl, influencerSignatureMeta, style, onPdfGenerated }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [containerDimensions, setContainerDimensions] = useState({ width: 800, height: '100%' });
  const [signaturePage, setSignaturePage] = useState(null);

  // Function to scroll to signature page
  const scrollToSignaturePage = () => {
    if (signaturePage) {
      const pageElement = document.querySelector(`[data-page-number="${signaturePage}"]`);
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Update signature page when signature meta changes
  useEffect(() => {
    if (influencerSignatureMeta?.position?.page) {
      setSignaturePage(influencerSignatureMeta.position.page);
    }
  }, [influencerSignatureMeta]);

  // Scroll to signature page after PDF loads
  useEffect(() => {
    if (signaturePage && !isGenerating) {
      // Small delay to ensure PDF is rendered
      const timeoutId = setTimeout(scrollToSignaturePage, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [signaturePage, isGenerating]);

  const fetchFontBytes = async (fontFamily) => {
    try {
      const fontUrl = FONT_URLS[fontFamily];
      if (!fontUrl) {
        console.warn(`Font ${fontFamily} not found, falling back to Helvetica`);
        return null;
      }

      // Handle local font files differently
      if (typeof fontUrl === 'string' && fontUrl.startsWith('http')) {
        const response = await fetch(fontUrl);
        if (!response.ok) throw new Error(`Failed to fetch font ${fontFamily}`);
        return await response.arrayBuffer();
      } else {
        // For local font files (Alex Brush and Ms Madi)
        return fontUrl;
      }
    } catch (error) {
      console.error('Error fetching font:', error);
      return null;
    }
  };

  const generatePDF = async () => {
    if (!pdfUrl) {
      console.error('No PDF URL provided');
      return null;
    }

    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error('Failed to fetch PDF');
      const pdfBytes = await response.arrayBuffer();

      // If no signature, return original PDF
      if (!influencerSignatureMeta?.position) {
        return new Blob([pdfBytes], { type: 'application/pdf' });
      }

      const pdfDoc = await PDFDocument.load(pdfBytes);
      pdfDoc.registerFontkit(fontkit);

      const page = pdfDoc.getPages()[influencerSignatureMeta.position.page - 1];
      if (!page) {
        console.error('Page not found');
        return new Blob([pdfBytes], { type: 'application/pdf' });
      }

      const { height } = page.getSize();
      const { x, y, width, height: fieldHeight } = influencerSignatureMeta.position;
      const pdfY = height - y - fieldHeight;

      if (influencerSignatureMeta.type === 'typed') {
        // Pre-fetch font before PDF generation
        let signatureFont;
        if (influencerSignatureMeta.fontFamily) {
          const fontBytes = await fetchFontBytes(influencerSignatureMeta.fontFamily);
          try {
            if (fontBytes) {
              signatureFont = await pdfDoc.embedFont(fontBytes);
            }
          } catch (error) {
            console.error('Error embedding font:', error);
            signatureFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
          }
        }
        if (!signatureFont) {
          signatureFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
        }

        page.drawText(influencerSignatureMeta.text || '', {
          x,
          y: pdfY,
          size: fieldHeight * 0.75,
          font: signatureFont,
          color: rgb(0, 0, 0),
        });
      } else if (influencerSignatureMeta.type === 'image' && influencerSignatureMeta.src) {
        try {
          // Pre-fetch image before PDF generation
          let imageBytes;
          const imageResponse = await fetch(influencerSignatureMeta.src);
          if (!imageResponse.ok) throw new Error('Failed to fetch signature image');
          imageBytes = await imageResponse.arrayBuffer();

          let signatureImage;
          const isPng = influencerSignatureMeta.src.toLowerCase().includes('png');
          try {
            signatureImage = isPng ? await pdfDoc.embedPng(imageBytes) : await pdfDoc.embedJpg(imageBytes);
          } catch (error) {
            console.error('Error embedding image:', error);
            return new Blob([pdfBytes], { type: 'application/pdf' });
          }

          const scaleFactor = Math.min(width / signatureImage.width, fieldHeight / signatureImage.height);
          const scaledWidth = signatureImage.width * scaleFactor;
          const scaledHeight = signatureImage.height * scaleFactor;

          const xOffset = (width - scaledWidth) / 2;
          const yOffset = (fieldHeight - scaledHeight) / 2;

          page.drawImage(signatureImage, {
            x: x + xOffset,
            y: pdfY + yOffset,
            width: scaledWidth,
            height: scaledHeight,
          });
        } catch (error) {
          console.error('Error processing signature image:', error);
          return new Blob([pdfBytes], { type: 'application/pdf' });
        }
      }

      const modifiedPdfBytes = await pdfDoc.save();
      return new Blob([modifiedPdfBytes], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      return null;
    }
  };

  // Update container dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      const container = document.querySelector('.pdf-preview-content');
      if (container) {
        setContainerDimensions({
          width: container.clientWidth,
          height: container.clientHeight,
        });
      }
    };

    // Initial update
    updateDimensions();

    // Add resize listener
    window.addEventListener('resize', updateDimensions);

    // Cleanup
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const updatePDF = async () => {
      if (!pdfUrl) return;

      setIsGenerating(true);
      try {
        const generatedBlob = await generatePDF();

        if (!isMounted) return;

        if (generatedBlob) {
          const url = URL.createObjectURL(generatedBlob);
          setPreviewUrl(url);
          if (onPdfGenerated) {
            onPdfGenerated(generatedBlob);
          }
        } else {
          setPreviewUrl(pdfUrl);
        }
      } catch (error) {
        console.error('Error updating PDF:', error);
        if (isMounted) {
          setPreviewUrl(pdfUrl);
        }
      } finally {
        if (isMounted) {
          setIsGenerating(false);
        }
      }
    };

    updatePDF();

    return () => {
      isMounted = false;
      if (previewUrl && previewUrl !== pdfUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [pdfUrl, influencerSignatureMeta]);

  if (!pdfUrl) {
    return <div>No PDF URL provided</div>;
  }

  return (
    <div
      className="pdf-preview-content"
      style={{
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        ...style,
      }}>
      {isGenerating ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: 20 }}>
          <SaralLoaderImage />
        </div>
      ) : (
        <Document
          file={previewUrl || pdfUrl}
          onLoadSuccess={({ numPages }) => {
            setNumPages(numPages);
            // Scroll to signature page after PDF loads
            if (signaturePage) {
              setTimeout(scrollToSignaturePage, 500);
            }
          }}
          loading={
            <div
              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: 20 }}>
              <SaralLoaderImage />
            </div>
          }
          error={<div>Error loading PDF</div>}>
          {Array.from(new Array(numPages), (el, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              width={Math.min(containerDimensions.width - 48, 800)}
              style={{ margin: '12px auto' }}
              data-page-number={index + 1} // Add data attribute for scrolling
            />
          ))}
        </Document>
      )}
    </div>
  );
};

export default PDFAnnotator;
